import { IPlayerProps, ISceneBlock, ISendEvents, Id } from '../../../interfaces';
import { SceneMimeTypes } from '../scene-blocks/mime-constants';
import { MatDialog } from '@angular/material/dialog';
import { Injector } from '@angular/core';
import { DialogTouchActionComponent } from './touch-action-dialog/touch-action-dialog.component';
import { DialogSwitchConfirmComponent, SwitchConfirmDialogData } from './dialog-switch-confirm/dialog-switch-confirm.component';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { StoreSelectorsService } from "../../../services";
import { AppService } from "../../../services";
import { actionUpdateSceneItem } from '../../../store/actions/user.actions';
import { take } from 'rxjs/operators';
import * as _ from 'lodash';

export interface Slide {
    id: number;
    title: string;
    privateName: string;
    sceneId?: string;
}

export interface SliderCreationSettings {
    width: number;
    height: number;
    name: string;
}

export type NavigationButtonType = "first" | "prev" | "next" | "last";
export type NavigationButtonSize = 1 | 2 | 3 | 4 | 5;

export class TouchLib {
    
    static createSlide(
        slideIndex: number, 
        title: string, 
        privateName: string,
        sceneId?: string
    ): Slide {
        return {
            id: slideIndex,
            title,
            privateName,
            sceneId
        };
    }

    static getRandomIconName(): string {
        const icons = [
            'star', 'favorite', 'home', 'person', 'music_note', 
            'photo_camera', 'image', 'notifications', 'event', 'schedule', 
            'cloud', 'comment', 'visibility', 'flag', 'folder', 
            'public', 'info', 'lock', 'map', 'phone', 
            'search', 'share', 'local_offer', 'thumb_up', 'emoji_events', 
            'videocam', 'wifi', 'card_giftcard', 'rocket_launch', 'sentiment_satisfied'
        ];
        return icons[Math.floor(Math.random() * icons.length)];
    }

    static getSliderDimensions(sliderScene: IPlayerProps): { width: string; height: string } {
        if (!sliderScene) return { width: '1920', height: '1080' };
        
        return {
            width: sliderScene.Player.Data[0].Layout[0].$.width,
            height: sliderScene.Player.Data[0].Layout[0].$.height
        };
    }

    static getSliderBasicInfo(sliderScene: IPlayerProps): { 
        sceneId: string; 
        name: string; 
        width: string; 
        height: string; 
    } {
        if (!sliderScene) return { 
            sceneId: '', 
            name: '', 
            width: '1920', 
            height: '1080' 
        };

        const dimensions = this.getSliderDimensions(sliderScene);
        
        return {
            sceneId: sliderScene.Player.$.id,
            name: sliderScene.Player.$.label,
            width: dimensions.width,
            height: dimensions.height
        };
    }

    static extractSlidesFromSlider(sliderScene: IPlayerProps): Slide[] {
        if (!sliderScene) return [];

        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        if (!players || players.length === 0) return [];

        const componentName = this.getComponentName(sliderScene);
        const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
        if (!componentPlayer) return [];

        const pages = componentPlayer.Data[0][componentName][0].Page;
        if (!pages || pages.length === 0) return [];

        return pages.map((p, ind) => {
            const sceneId = p?.Player?.[0]?.$?.src;

            // Use the real scene label if available, otherwise fallback
            const sceneTitle = `slide ${ind + 1}`

            return this.createSlide(ind, sceneTitle, p.$.page, sceneId);
        }).filter((slide) => !!slide.sceneId);
    }

    static getPlaylistId(sliderScene: IPlayerProps): string | null {
        if (!sliderScene) return null;

        try {
            const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            if (!players || players.length === 0) return null;
            const componentName = this.getComponentName(sliderScene);
            const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
            return componentPlayer?.$.id || null;
        } catch (error) {
            console.warn('Could not extract playlist ID:', error);
            return null;
        }
    }

    static getCurrentPages(sliderScene: IPlayerProps): any[] {
        if (!sliderScene) return [];

        try {
            const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            if (!players || players.length === 0) return [];
            const componentName = this.getComponentName(sliderScene);
            const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
            if (!componentPlayer) return [];

            return _.cloneDeep(componentPlayer.Data[0][componentName][0].Page || []);
        } catch (error) {
            console.warn('Could not extract pages:', error);
            return [];
        }
    }

    static createNewSlideScene(
        width: string,
        height: string,
        blockLabels: any,
        newSlideName: string,
        labelText?: string
    ): { scene: any; sceneId: string } {
        const subSceneId = _.uniqueId();
        const subScene: ISceneBlock = _.cloneDeep(blockLabels.BLOCKCODE_SCENE.template);
        const newSubScene = { Player: subScene };

        newSubScene.Player.$.id = subSceneId;
        newSubScene.Player.Data[0].Layout[0].$.width = width;
        newSubScene.Player.Data[0].Layout[0].$.height = height;
        newSubScene.Player.$.label = newSlideName;
        newSubScene.Player.$.mimeType = SceneMimeTypes.Scene;
        newSubScene.Player.$.private_child = true;
        if (newSubScene.Player.Data[0].Animation) {
            delete newSubScene.Player.Data[0].Animation;
        }

        const sceneWidth = parseInt(width);
        const sceneHeight = parseInt(height);
        
        // Positioning constants
        const iconSize = 250;
        const iconLabelGap = 40;
        const estimatedTextWidth = 800; // Estimated width for "New Slide XX" at font size 100
        const totalContentWidth = iconSize + iconLabelGap + estimatedTextWidth;
        
        const startX = (sceneWidth - totalContentWidth) / 2;
        const verticalCenter = sceneHeight / 2;
        const iconY = verticalCenter - (iconSize / 2);

        // Icon settings
        const iconBlockData: ISceneBlock = _.cloneDeep(blockLabels.ICON.template);
        iconBlockData.$.id = _.uniqueId("__");
        const iconLayout = iconBlockData.Data["0"].Layout["0"]?.$;
        iconLayout.width = `${iconSize}`;
        iconLayout.height = `${iconSize}`;
        iconLayout.x = `${startX}`;
        iconLayout.y = `${iconY}`;
        // Randomly choose an icon
        iconBlockData.Data["0"].Icon["0"].$.name = this.getRandomIconName();
        // Set icon color to blue
        if (iconBlockData.Data["0"].Icon["0"].$) {
             iconBlockData.Data["0"].Icon["0"].$.color = "2201843"; // Blue color
        }

        // Label settings
        const labelBlockData: ISceneBlock = _.cloneDeep(blockLabels.LABEL.template);
        labelBlockData.$.id = _.uniqueId("__");
        const layout = labelBlockData.Data["0"].Layout["0"]?.$;
        layout.width = `${estimatedTextWidth}`; 
        layout.height = `${iconSize}`; // Match icon height
        layout.x = `${startX + iconSize + iconLabelGap}`; // Right after the icon with a gap
        layout.y = `${iconY}`; // Vertically aligned with icon
        
        const font = labelBlockData.Data["0"].Label["0"];
        font.Font["0"].$.fontSize = "100"; // Bigger font
        font.Font["0"].$.fontColor = "2201843"; // Blue color
        font.Font["0"].$.textAlign = "left"; // Align left next to icon
        font.Text = [labelText || "New Slide"];

        newSubScene.Player.Data[0].Scene[0].Players[0].Player = [iconBlockData, labelBlockData];

        return {
            scene: newSubScene,
            sceneId: subSceneId
        };
    }

    static createPlaylistPage(slideSceneLabel: string, slideSceneId: string): any {
        return {
            $: {
                page: slideSceneLabel,
                type: "scene",
            },
            Player: [{
                $: {
                    src: slideSceneId,
                },
            }],
        };
    }

    static createSelectedSliderPayload(
        sceneId: string,
        sceneTitle: string,
        firstChildSceneId?: string | null
    ): any {
        return {
            selectedTouch: {
                sceneId,
                SceneTitle: sceneTitle,
                ScenePlaylistId: firstChildSceneId || null
            }
        };
    }

    /**
     * Detect navigation menu settings from existing blocks in the scene
     * Used to restore state on reload so that menu sync continues to work
     */
    static detectNavigationMenuSettings(sliderScene: IPlayerProps): { orientation: 'horizontal' | 'vertical'; size: number } | null {
        if (!sliderScene) return null;

        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        if (!players || players.length === 0) return null;

        // Find navigation menu blocks
        const menuBlocks = players.filter(p => p.$.group === 'slide_nav_menu');
        if (!menuBlocks || menuBlocks.length === 0) return null;

        // Infer settings from the first block
        const firstBlock = menuBlocks[0];
        const layout = firstBlock.Data?.[0]?.Layout?.[0]?.$;
        if (!layout) return null;

        const width = parseInt(layout.width);
        const y = parseInt(layout.y);
        
        // Infer size from width
        // 1: 80, 2: 100, 3: 120, 4: 140, 5: 160
        let size = 2; // Default
        if (width <= 80) size = 1;
        else if (width <= 100) size = 2;
        else if (width <= 120) size = 3;
        else if (width <= 140) size = 4;
        else size = 5;

        // Infer orientation from position or multiple blocks
        let orientation: 'horizontal' | 'vertical' = 'horizontal'; // Default

        if (menuBlocks.length > 1) {
            const secondBlock = menuBlocks[1];
            const secondLayout = secondBlock.Data?.[0]?.Layout?.[0]?.$;
            
            if (secondLayout) {
                // If y is same (or very close), it's horizontal
                const y1 = parseInt(layout.y);
                const y2 = parseInt(secondLayout.y);
                if (Math.abs(y1 - y2) < 5) {
                    orientation = 'horizontal';
                } else {
                    orientation = 'vertical';
                }
            }
        } else {
            // Only one block, guess based on position
            // Horizontal uses y = 30 (margin)
            // Vertical uses y = 30 + index * ...
            // Vertical x = width - margin - w
            
            // If y is close to margin (30), likely horizontal
            if (Math.abs(y - 30) < 5) {
                orientation = 'horizontal';
            } else {
                // Check if x is at the right edge
                const sceneWidth = parseInt(this.getSliderDimensions(sliderScene).width);
                const x = parseInt(layout.x);
                // Vertical x = width - 30 - width_of_block
                if (Math.abs(x - (sceneWidth - 30 - width)) < 5) {
                    orientation = 'vertical';
                }
            }
        }

        return { orientation, size };
    }

    /**
     * Handles smooth slide transition with masking effect to prevent jerkiness
     * @param slideSceneId - The scene ID of the slide to transition to
     * @param setTransitioningSlideId - Function to set the transitioning slide ID
     * @param triggerChangeDetection - Function to trigger Angular change detection
     * @returns Promise that resolves when transition is complete
     */
    static async handleSmoothSlideTransition(
        slideSceneId: string,
        setTransitioningSlideId: (id: string | null) => void,
        triggerChangeDetection: () => void
    ): Promise<void> {
        // Capture the current slide dimensions before hiding it
        const slideElement = document.querySelector(`[data-slide-id="${slideSceneId}"] .slide-smooth-wrapper`);
        if (slideElement) {
            const rect = slideElement.getBoundingClientRect();
            // Store dimensions on the element for CSS to use
            (slideElement as HTMLElement).style.setProperty('--captured-width', `${rect.width}px`);
            (slideElement as HTMLElement).style.setProperty('--captured-height', `${rect.height}px`);
        }
        
        // Start masking transition - hide the slide during resize
        setTransitioningSlideId(slideSceneId);
        triggerChangeDetection();
        
        // Return a promise that resolves when the transition is complete
        return new Promise((resolve) => {
            setTimeout(() => {
                setTransitioningSlideId(null);
                triggerChangeDetection();
                resolve();
            }, 150); // Optimized to 150ms for minimal but smooth transition
        });
    }

    /**
     * Get duration for playlist
     * @param sliderScene
     * @returns
     */
    static getDuration(sliderScene: IPlayerProps) {
        if (!sliderScene) return '0';
        const componentName = this.getComponentName(sliderScene);
        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);

        return componentPlayer?.Data?.[0]?.[componentName]?.[0]?.$?.duration || '0';
    }

    /**
     * Get duration for playlist
     * @param sliderScene
     * @returns
     */
    static getPagesLength(sliderScene: IPlayerProps) {
        if (!sliderScene) return 0;
        const componentName = this.getComponentName(sliderScene);
        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);

        return componentPlayer?.Data?.[0]?.[componentName]?.[0]?.Page?.length;
    }

    /**
     * check if the slider is collection or playlist
     * @param sliderScene
     * @returns
     */
    static isCollection(sliderScene: IPlayerProps) {
        if (!sliderScene) return true;
        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        if (!players || players.length === 0) return true;

        const componentPlayer = players.find(p => p.Data?.[0]?.Collection || p.Data?.[0]?.Playlist);
        if (!componentPlayer) return true;

        return !!componentPlayer.Data[0].Collection;
    }

    /**
     * Get slider component name
     * @param sliderScene
     * @returns: 'Collection' | 'Playlist'
     */
    static getComponentName(sliderScene: IPlayerProps): 'Collection' | 'Playlist' {
        return this.isCollection(sliderScene) ? 'Collection': 'Playlist';
    }

    static getFirstSlideSceneId(sliderScene: IPlayerProps) {
        let firstChildSceneId: string | null = null;
        try {
            const componentName = this.getComponentName(sliderScene);
            const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);

            const firstPage = componentPlayer?.Data?.[0]?.[componentName]?.[0]?.Page?.[0];
            if (firstPage && firstPage.Player?.[0]?.$.src) {
                firstChildSceneId = firstPage.Player[0].$.src;
            }
        } catch (error) {
            console.warn("Could not extract first child scene ID:", error);
        }
        return firstChildSceneId;
    }

    /**
     * Find the parent Touch scene (wrapper) that contains a specific child scene ID
     * @param childSceneId - The scene ID to search for
     * @param allScenes - Array of all scenes to search through
     * @returns The parent Touch scene if found, null otherwise
     */
    static findParentTouchScene(childSceneId: string, allScenes: IPlayerProps[]): IPlayerProps | null {
        if (!childSceneId || !allScenes) return null;

        for (const scene of allScenes) {
            if (scene.Player?.$.mimeType !== SceneMimeTypes.Slides) continue;
            if (!scene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player) continue;

            try {
                const componentName = this.getComponentName(scene);
                const players = scene.Player.Data[0].Scene[0].Players[0].Player;
                const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
                if (!componentPlayer) continue;

                const pages = componentPlayer.Data[0][componentName][0].Page;

                if (pages && pages.length > 0) {
                    const foundChild = pages.find((page) => {
                        return page?.Player?.[0]?.$?.src === childSceneId;
                    });

                    if (foundChild) {
                        return scene;
                    }
                }
            } catch (error) {
                continue;
            }
        }

        return null;
    }

    static uniqueName(prefix = null) {
        if (!prefix) prefix = 'slide';
        return `${prefix}_${Math.random().toString(36).substring(2, 6)}`;
    }

    static touchEventName(command) {
        if (command === 'selectPage') {
            return `touch_${Math.random().toString(36).substring(2, 6)}`;
        }
        return `touch_${command}`;
    }

    
    static getSendEventTemplate(): ISendEvents {
        return {
            $: {
                from: "",
                command: "",
            },
            Params: [
                {
                    Page: [
                        {
                            $: {
                                name: "",
                                page: "",
                                type: "",
                            },
                            Player: [
                                {
                                    $: {},
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Find the slider scene that contains the given scene ID
     * Checks both if the scene IS a slider and if it's contained IN a slider
     */
    static findSliderSceneForComponent(sceneId: string, allSliders: IPlayerProps[]): IPlayerProps | null {
        if (!sceneId || !allSliders) return null;

        // First, check if the current scene IS one of the sliders
        const currentSceneIsSlider = allSliders.find(slider => slider.Player.$.id === sceneId);
        if (currentSceneIsSlider) {
            return currentSceneIsSlider;
        }

        // If not, find the parent slider that contains the current scene
        return allSliders.find(slider => {
            const players = slider?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            if (!players || players.length === 0) return false;

            const componentName = this.getComponentName(slider);
            const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
            if (!componentPlayer) return false;

            const pages = componentPlayer.Data[0][componentName][0].Page;
            if (!pages || pages.length === 0) return false;

            // Check if current scene is in this slider's pages
            return pages.some(page =>
                page?.Player?.[0]?.$?.src === sceneId
            );
        }) || null;
    }

    /**
     * Handle touch action for components (label, button, image, icon)
     * Checks if we're in Playlist mode and prompts user to switch to Collection if needed
     * This is the main entry point for touch actions from components
     */
    static handleTouchAction(sceneId: string, dialog: MatDialog, injector: Injector) {
        try {
            const storeSelector = injector.get(StoreSelectorsService);
            const toastr = injector.get(ToastrService);
            const translocoService = injector.get(TranslocoService);
            const appService = injector.get(AppService);

            storeSelector.getTouchScenes().pipe(
                take(1)
            ).subscribe(sliders => {
                // Find the slider scene
                const sliderScene = this.findSliderSceneForComponent(sceneId, sliders);
                if (!sliderScene) return;

                // Check if we're in Playlist mode - touch events only work in Collection mode
                const componentName = this.getComponentName(sliderScene);
                if (componentName === 'Playlist') {
                    // Show dialog prompting user to switch to Collection mode
                    this.promptSwitchToCollection(sliderScene, dialog, storeSelector, toastr, translocoService, appService);
                    return;
                }

                // We're in Collection mode, proceed with touch action dialog
                this.openTouchActionDialog(sliderScene, dialog);
            });
        } catch (error) {
            console.error('Error handling touch action:', error);
        }
    }

    private static promptSwitchToCollection(
        sliderScene: IPlayerProps,
        dialog: MatDialog,
        storeSelector: StoreSelectorsService,
        toastr: ToastrService,
        translocoService: TranslocoService,
        appService: AppService
    ) {
        const dialogData: SwitchConfirmDialogData = {
            fromComponent: 'Playlist',
            toComponent: 'Collection'
        };

        const dialogRef = dialog.open(DialogSwitchConfirmComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                // User cancelled - show info message
                toastr.info(
                    translocoService.translate('ai.touchEventsRequireCollection', {}, translocoService.getActiveLang())
                );
                return;
            }

            // User confirmed - switch from Playlist to Collection
            this.switchPlaylistToCollection(sliderScene, dialog, storeSelector, toastr, translocoService, appService);
        });
    }

    private static switchPlaylistToCollection(
        sliderScene: IPlayerProps,
        dialog: MatDialog,
        storeSelector: StoreSelectorsService,
        toastr: ToastrService,
        translocoService: TranslocoService,
        appService: AppService
    ) {
        const sliderSceneId = sliderScene.Player.$.id;
        const players = sliderScene.Player.Data[0].Scene[0].Players[0].Player;
        const playlistBlock = players.find(p => p.Data?.[0]?.Playlist);

        if (!playlistBlock) {
            toastr.error(
                translocoService.translate('ai.couldNotFindCollectionPlaylist', {}, translocoService.getActiveLang())
            );
            return;
        }

        const playlistPages = playlistBlock.Data[0].Playlist[0].Page;
        const playlistLayout = playlistBlock.Data[0].Layout;

        // Clone Collection template
        const collectionBlockData = _.cloneDeep(storeSelector.blockLabels.COLLECTION.template);
        collectionBlockData.Data[0].Layout = playlistLayout;
        collectionBlockData.$.id = playlistBlock.$.id;
        collectionBlockData.Data[0].Collection[0].Page = playlistPages;

        // Replace Playlist with Collection
        const playlistBlockIndex = players.findIndex(p => p.$.id === playlistBlock.$.id);
        const finalPlayersArray = [...players];
        finalPlayersArray[playlistBlockIndex] = collectionBlockData;

        storeSelector.dispatch(
            actionUpdateSceneItem({
                sceneId: sliderSceneId,
                sceneItemId: sliderSceneId,
                values: [
                    {
                        key: "Player",
                        value: finalPlayersArray,
                        storePath: "Data[0].Scene[0].Players[0]",
                    },
                ],
                disableCRUD: true,
            })
        );

        setTimeout(() => {
            appService.updateMultiScene([sliderSceneId]);
            toastr.success(
                translocoService.translate('ai.switchedToCollection', {}, translocoService.getActiveLang())
            );

            // After switching, open the touch action dialog
            setTimeout(() => {
                // Re-fetch the updated slider scene
                storeSelector.getTouchScenes().pipe(
                    take(1)
                ).subscribe(sliders => {
                    const updatedSliderScene = sliders.find(slider => slider.Player.$.id === sliderSceneId);
                    if (updatedSliderScene) {
                        this.openTouchActionDialog(updatedSliderScene, dialog);
                    }
                });
            }, 500);
        });
    }

    private static openTouchActionDialog(sliderScene: IPlayerProps, dialog: MatDialog) {
        dialog.open(DialogTouchActionComponent, {
            width: '500px',
            data: {
                sliderScene: sliderScene,
                newTouchButton: false,
                offset: null
            }
        });
    }
}
